import React from 'react';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const MAX_SIZE = 20 * 1024 * 1024; // 20 MB
const CV_EXT = ['pdf', 'doc', 'docx'];
const PORTFOLIO_EXT = ['pdf', 'doc', 'docx', 'zip', 'ppt', 'pptx', 'png', 'jpg', 'jpeg'];

const checkFile = (file, allowedExt, maxSize = MAX_SIZE) => {
  if (!file) return null;
  if (file.size > maxSize) return `Fichier trop volumineux (max ${Math.round(maxSize / 1024 / 1024)} Mo).`;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!allowedExt.includes(ext)) {
    return `Format non accepté. Formats autorisés : ${allowedExt.join(', ').toUpperCase()}.`;
  }
  return null;
};

const errBorder = (err) => (err ? 'border-red-500 focus-visible:ring-red-400' : '');
const ErrorMsg = ({ err, field }) =>
  err ? (
    <p className="text-xs text-red-600 mt-1 flex items-start gap-1" data-testid={`error-${field}`}>
      <span aria-hidden>⚠️</span>
      <span>{err}</span>
    </p>
  ) : null;

const DocumentsSection = ({ formData, setFormData, fieldErrors = {} }) => (
  <div className="space-y-4">
    {/* CV Upload */}
    <div>
      <Label htmlFor="cv_file" className="text-tp-text">Télécharger votre CV (PDF ou DOCX) *</Label>
      <Input
        id="cv_file"
        type="file"
        accept=".pdf,.docx,.doc"
        required
        onChange={(e) => {
          const file = e.target.files[0];
          if (!file) return;
          const err = checkFile(file, CV_EXT);
          if (err) {
            toast.error(err);
            e.target.value = '';
            setFormData({ ...formData, cv_file: null });
            return;
          }
          setFormData({ ...formData, cv_file: file });
        }}
        className={errBorder(fieldErrors.cv_file)}
        aria-invalid={!!fieldErrors.cv_file}
        data-testid="input-cv"
      />
      <p className="text-xs text-tp-text-light mt-1">PDF, DOC ou DOCX — Taille maximale : 20 Mo</p>
      <ErrorMsg err={fieldErrors.cv_file} field="cv_file" />
    </div>

    {/* Portfolio */}
    <div>
      <Label htmlFor="portfolio_url" className="text-tp-text">Portfolio</Label>
      <Input
        id="portfolio_url"
        type="url"
        placeholder="https://votre-portfolio.com"
        value={formData.portfolio_url}
        onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
        className={errBorder(fieldErrors.portfolio_url)}
        data-testid="input-portfolio"
      />
      <ErrorMsg err={fieldErrors.portfolio_url} field="portfolio_url" />
      <p className="text-xs text-tp-text-light mt-1 mb-2">Ou téléchargez un fichier portfolio :</p>
      <Input
        id="portfolio_file"
        type="file"
        accept=".pdf,.docx,.doc,.zip,.pptx,.ppt,.png,.jpg,.jpeg"
        onChange={(e) => {
          const file = e.target.files[0];
          if (!file) return;
          const err = checkFile(file, PORTFOLIO_EXT);
          if (err) {
            toast.error(err);
            e.target.value = '';
            setFormData({ ...formData, portfolio_file: null });
            return;
          }
          setFormData({ ...formData, portfolio_file: file });
        }}
        data-testid="input-portfolio-file"
      />
      <p className="text-xs text-tp-text-light mt-1">PDF, DOCX, ZIP, PPTX, images — Max 20 Mo</p>
    </div>

    {/* Social profiles */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor="linkedin_profile" className="text-tp-text">Profil LinkedIn</Label>
        <Input
          id="linkedin_profile"
          type="url"
          placeholder="https://linkedin.com/in/..."
          value={formData.linkedin_profile}
          onChange={(e) => setFormData({ ...formData, linkedin_profile: e.target.value })}
          data-testid="input-linkedin"
        />
      </div>
      <div>
        <Label htmlFor="instagram_profile" className="text-tp-text">Instagram</Label>
        <Input
          id="instagram_profile"
          placeholder="@votre_instagram"
          value={formData.instagram_profile}
          onChange={(e) => setFormData({ ...formData, instagram_profile: e.target.value })}
          data-testid="input-instagram"
        />
      </div>
      <div>
        <Label htmlFor="facebook_profile" className="text-tp-text">Facebook</Label>
        <Input
          id="facebook_profile"
          placeholder="URL de votre profil Facebook"
          value={formData.facebook_profile}
          onChange={(e) => setFormData({ ...formData, facebook_profile: e.target.value })}
          data-testid="input-facebook"
        />
      </div>
    </div>

    {/* Motivation */}
    <div>
      <Label htmlFor="motivation_text" className="text-tp-text">Motivation (optionnel)</Label>
      <Textarea
        id="motivation_text"
        placeholder="Pourquoi souhaitez-vous rejoindre notre réseau de talents ?"
        value={formData.motivation_text}
        onChange={(e) => setFormData({ ...formData, motivation_text: e.target.value })}
        rows={4}
        data-testid="input-motivation"
      />
    </div>

    {/* Referral Source */}
    <div>
      <Label htmlFor="referral_source" className="text-tp-text">Comment nous avez-vous connu ? *</Label>
      <select
        id="referral_source"
        required
        value={formData.referral_source}
        onChange={(e) => setFormData({ ...formData, referral_source: e.target.value })}
        className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${fieldErrors.referral_source ? 'border-red-500 focus-visible:ring-red-400' : 'border-input'}`}
        aria-invalid={!!fieldErrors.referral_source}
        data-testid="select-referral-source"
      >
        <option value="">Sélectionnez une option</option>
        <option value="word_of_mouth">Bouche à oreille</option>
        <option value="google_search">Recherche Google</option>
        <option value="facebook_group">Groupe Facebook</option>
        <option value="facebook_ad">Publicité Facebook</option>
        <option value="instagram_ad">Publicité Instagram</option>
        <option value="linkedin">LinkedIn</option>
        <option value="job_board">Site d'emploi</option>
        <option value="other">Autre</option>
      </select>
      <ErrorMsg err={fieldErrors.referral_source} field="referral_source" />
    </div>
  </div>
);

export default DocumentsSection;
